import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Loader2,
  Building2
} from 'lucide-react';
import PageContainer from '@/components/common/PageContainer';

interface Vendor {
  id: string;
  name: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

const VendorForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [formName, setFormName] = useState('');
  const [formStatus, setFormStatus] = useState('active');

  useEffect(() => {
    if (isEditMode) {
      fetchVendor();
    }
  }, [id]);

  const fetchVendor = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      if (data) {
        setFormName(data.name || '');
        setFormStatus(data.status || 'active');
      }
    } catch (error) {
      console.error('Erro ao buscar fornecedor:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados do fornecedor.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formName.trim()) {
      toast({
        title: 'Erro',
        description: 'O nome do fornecedor é obrigatório.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const vendorData = {
        name: formName.trim(),
        status: formStatus
      };

      let result;
      
      if (isEditMode) {
        result = await supabase
          .from('vendors')
          .update(vendorData)
          .eq('id', id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('vendors')
          .insert(vendorData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      toast({
        title: 'Sucesso',
        description: isEditMode 
          ? 'Fornecedor atualizado com sucesso!' 
          : 'Fornecedor criado com sucesso!',
        variant: 'default'
      });

      navigate('/vendors');
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      toast({
        title: 'Erro',
        description: `Erro ao salvar fornecedor: ${error instanceof Error ? error.message : 'Erro desconhecido'}`,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendors');
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={handleCancel}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-extrabold">
                {isEditMode ? 'Editar Fornecedor' : 'Novo Fornecedor'}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode 
                  ? 'Atualize as informações do fornecedor' 
                  : 'Crie um novo fornecedor no sistema'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informações do Fornecedor
                </CardTitle>
                <CardDescription>
                  Preencha as informações básicas do fornecedor
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Fornecedor *</Label>
                  <Input
                    id="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Digite o nome do fornecedor"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formStatus} onValueChange={setFormStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditMode ? 'Atualizar' : 'Criar'} Fornecedor
            </Button>
          </div>
        </form>
      </div>
    </PageContainer>
  );
};

export default VendorForm;
